export const PRESENCE_TTL_MS = 35_000;
export const MAX_NICKNAME_LENGTH = 24;

const UUID_V4_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;

export interface PresenceHeartbeat {
  profileId: string;
  presenceId: string;
  nickname: string;
  searching: boolean;
}

/** Account heartbeat deliberately omits browser-controlled profile and nickname fields. */
export interface AuthenticatedPresenceHeartbeat {
  presenceId: string;
  searching: boolean;
}

export type PresenceAggregationKind = 'anonymous' | 'account';

export interface PresenceSearcher {
  nickname: string;
  searchingSince: number;
}

export interface PresenceSnapshot {
  onlineCount: number;
  searchingCount: number;
  searchers: PresenceSearcher[];
  serverTime: number;
}

/** Process-local transition used for operator notifications; never sent to browsers. */
export interface PresenceSearchChange extends PresenceSearcher {
  profileKey: string;
  kind: 'started' | 'stopped';
}

interface PresenceEntry extends PresenceHeartbeat {
  aggregationKind: PresenceAggregationKind;
  updatedAt: number;
  searchingSince?: number;
}

export function normalizeNickname(value: string): string {
  return value.normalize('NFKC').trim().replace(/\s+/gu, ' ');
}

export function validatePresenceHeartbeat(value: unknown): PresenceHeartbeat {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error('heartbeat должен быть JSON-объектом');
  }
  const record = value as Record<string, unknown>;
  const keys = Object.keys(record);
  if (keys.some((key) => !['profileId', 'presenceId', 'nickname', 'searching'].includes(key))) {
    throw new Error('heartbeat содержит неизвестные поля');
  }
  if (typeof record.profileId !== 'string' || !UUID_V4_PATTERN.test(record.profileId)) {
    throw new Error('profileId должен быть UUID v4');
  }
  if (typeof record.presenceId !== 'string' || !UUID_V4_PATTERN.test(record.presenceId)) {
    throw new Error('presenceId должен быть UUID v4');
  }
  if (typeof record.nickname !== 'string') throw new Error('nickname должен быть строкой');
  const nickname = normalizeNickname(record.nickname);
  if (Array.from(nickname).length > MAX_NICKNAME_LENGTH) {
    throw new Error(`nickname должен быть не длиннее ${MAX_NICKNAME_LENGTH} символов`);
  }
  if (/\p{C}/u.test(nickname)) throw new Error('nickname содержит недопустимые символы');
  if (typeof record.searching !== 'boolean') throw new Error('searching должен быть boolean');
  if (record.searching && nickname.length === 0) throw new Error('для поиска требуется nickname');
  return {
    profileId: record.profileId,
    presenceId: record.presenceId,
    nickname,
    searching: record.searching,
  };
}

export function validateAuthenticatedPresenceHeartbeat(
  value: unknown,
): AuthenticatedPresenceHeartbeat {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error('authenticated heartbeat должен быть JSON-объектом');
  }
  const record = value as Record<string, unknown>;
  const keys = Object.keys(record);
  if (keys.some((key) => !['presenceId', 'searching'].includes(key))) {
    throw new Error('authenticated heartbeat содержит неизвестные поля');
  }
  if (typeof record.presenceId !== 'string' || !UUID_V4_PATTERN.test(record.presenceId)) {
    throw new Error('presenceId должен быть UUID v4');
  }
  if (typeof record.searching !== 'boolean') throw new Error('searching должен быть boolean');
  return { presenceId: record.presenceId, searching: record.searching };
}

/** Bounded, process-local lobby presence. Match rooms remain durable in PostgreSQL. */
export class PresenceRegistry {
  private readonly entries = new Map<string, PresenceEntry>();

  constructor(
    private readonly now: () => number = Date.now,
    private readonly ttlMs = PRESENCE_TTL_MS,
    private readonly maxEntries = 1_000,
    private readonly onSearchChange?: (change: PresenceSearchChange) => void,
  ) {
    if (!Number.isSafeInteger(ttlMs) || ttlMs <= 0) throw new Error('presence ttl должен быть положительным');
    if (!Number.isSafeInteger(maxEntries) || maxEntries <= 0) throw new Error('presence maxEntries должен быть положительным');
  }

  heartbeat(
    input: PresenceHeartbeat,
    aggregationKind: PresenceAggregationKind = 'anonymous',
  ): PresenceSnapshot {
    const now = this.now();
    this.expire(now);
    const searchesBefore = this.searchesByProfile();
    const entryKey = `${aggregationKind}:${input.presenceId}`;
    const previous = this.entries.get(entryKey);
    if (previous === undefined && this.entries.size >= this.maxEntries) this.evictOldest();
    // A tab changing auth state replaces its previous namespace immediately;
    // it must not be counted as both anonymous and account presence until TTL.
    this.entries.delete(`anonymous:${input.presenceId}`);
    this.entries.delete(`account:${input.presenceId}`);
    this.entries.set(entryKey, {
      ...input,
      aggregationKind,
      updatedAt: now,
      ...(input.searching
        ? { searchingSince: previous?.searching === true ? previous.searchingSince ?? now : now }
        : {}),
    });
    this.emitSearchChanges(searchesBefore, this.searchesByProfile());
    return this.buildSnapshot(now);
  }

  snapshot(): PresenceSnapshot {
    const now = this.now();
    this.expire(now);
    return this.buildSnapshot(now);
  }

  private buildSnapshot(now: number): PresenceSnapshot {
    const byProfile = this.entriesByProfile();
    const searchers = [...this.searchesByProfile(byProfile).values()]
      .sort((left, right) => left.searchingSince - right.searchingSince
        || left.nickname.localeCompare(right.nickname, 'ru'))
      .map(({ nickname, searchingSince }) => ({ nickname, searchingSince }));
    return {
      onlineCount: byProfile.size,
      searchingCount: searchers.length,
      searchers,
      serverTime: now,
    };
  }

  private entriesByProfile(): Map<string, PresenceEntry[]> {
    const byProfile = new Map<string, PresenceEntry[]>();
    for (const entry of this.entries.values()) {
      const profileKey = `${entry.aggregationKind}:${entry.profileId}`;
      const current = byProfile.get(profileKey) ?? [];
      current.push(entry);
      byProfile.set(profileKey, current);
    }
    return byProfile;
  }

  private searchesByProfile(
    byProfile: ReadonlyMap<string, readonly PresenceEntry[]> = this.entriesByProfile(),
  ): Map<string, PresenceSearcher> {
    const searches = new Map<string, PresenceSearcher>();
    for (const [profileKey, entries] of byProfile) {
      const searching = entries
        .filter((entry) => entry.searching && entry.nickname.length > 0)
        .sort((left, right) => right.updatedAt - left.updatedAt)[0];
      if (searching !== undefined) {
        searches.set(profileKey, {
          nickname: searching.nickname,
          searchingSince: Math.min(...entries
            .filter((entry) => entry.searching)
            .map((entry) => entry.searchingSince ?? entry.updatedAt)),
        });
      }
    }
    return searches;
  }

  private emitSearchChanges(
    before: ReadonlyMap<string, PresenceSearcher>,
    after: ReadonlyMap<string, PresenceSearcher>,
  ): void {
    if (this.onSearchChange === undefined) return;
    for (const [profileKey, searcher] of before) {
      if (!after.has(profileKey)) this.onSearchChange({ kind: 'stopped', profileKey, ...searcher });
    }
    for (const [profileKey, searcher] of after) {
      if (!before.has(profileKey)) this.onSearchChange({ kind: 'started', profileKey, ...searcher });
    }
  }

  private expire(now: number): void {
    const searchesBefore = this.searchesByProfile();
    for (const [presenceId, entry] of this.entries) {
      if (now - entry.updatedAt >= this.ttlMs) this.entries.delete(presenceId);
    }
    this.emitSearchChanges(searchesBefore, this.searchesByProfile());
  }

  private evictOldest(): void {
    let oldest: { key: string; entry: PresenceEntry } | undefined;
    for (const [key, entry] of this.entries) {
      if (oldest === undefined || entry.updatedAt < oldest.entry.updatedAt) {
        oldest = { key, entry };
      }
    }
    if (oldest !== undefined) this.entries.delete(oldest.key);
  }
}
